// Регистрация источников/слоёв базовой карты MapLibre - вынесена из
// MapView.vue (см. пояснение в constants.ts этой же папки).
//
// addBasemapLayers ниже - точка входа, вызывается один раз из setupMapLayers
// (MapView.vue) сразу после создания карты. Разбита на подфункции по
// разделам исходного addBasemapLayers, каждая вызывается ровно один раз, в
// ТОМ ЖЕ порядке, что и в исходнике - порядок addLayer() определяет
// z-порядок MapLibre, переставлять его нельзя. Порядок addSource() не
// влияет на рендер (источник просто должен существовать раньше первого
// addLayer, который на него ссылается), поэтому все addSource() собраны в
// одну addSources(), вызываемую первой - её внутренний порядок free-form.
import type * as maplibregl from "maplibre-gl";
import {
  AERIALWAYS_SOURCE,
  BOUNDARIES_SOURCE,
  BUILDINGS_ADMIN_SOURCE,
  BUILDINGS_SOURCE,
  CLIFFS_SOURCE,
  CONTOURS_SOURCE,
  EMPTY_FEATURES,
  HILLSHADE_SOURCE,
  LANDCOVER_SOURCE,
  MAP_FONT,
  PLACES_SOURCE,
  POIS_ADMIN_SOURCE,
  POIS_CLUSTER_LAYER,
  POIS_CLUSTER_SOURCE,
  POIS_SOURCE,
  POI_LOD_MIN_ZOOM,
  POWER_LINES_SOURCE,
  RIDGES_SOURCE,
  ROADS_SOURCE,
  terrainUrlBase,
  TERRAIN_SOURCE,
  TRAILS_SOURCE,
  vectorTileUrl,
  WATERWAYS_SOURCE,
} from "./constants";
import {
  CONTOUR_COLOR,
  CONTOUR_IS_INDEX,
  CONTOUR_LABEL_COLOR,
  clusterHiddenOpacity,
  fadeInFrom,
  TRAIL_DASH,
  trailWidthAt,
  waterwayWidthAt,
} from "./styleHelpers";
import {
  buildPassIconImage,
  buildPeakIconImage,
  buildSvgPathIconImage,
  PASS_ICON_IMAGE_ID,
  PEAK_ICON_IMAGE_ID,
  PEAK_ICON_DARK_IMAGE_ID,
  poiIconImageId,
  POI_COLOR,
  POI_ICON_SVG_PATHS,
} from "./poiIcons";
import {
  AERIALWAY_PATTERN_IMAGE_ID,
  buildAerialwayTickImage,
  buildCliffHachureImage,
  CLIFF_PATTERN_IMAGE_ID,
} from "./cliffAerialwayIcons";
import {
  buildPoiClusterGlyphs,
  buildPoiClusterImageExpression,
  buildPoiClusterProperties,
  registerPoiClusterImageResolver,
  type PoiClusterGlyph,
} from "./poiClusterIcons";
import { buildNameField, buildPassLabelField, buildPeakLabelField } from "./labels";
import { POI_DETAIL_MIN_ZOOM } from "./poiVisibility";

// Что нужно слоям подписей помимо самой карты - `t`/`lang` разрешаются один
// раз в MapView.vue перед вызовом addBasemapLayers (вся цепочка вызовов
// синхронная), а не читаются здесь напрямую из useI18n(), которого в этом
// чистом модуле нет. `poiClusterExcludedKinds` - список видов, у которых
// нет общей пиктограммы/подписи (POI_CLUSTER_EXCLUDED_KINDS в MapView.vue,
// зависит от PIN_POI_KINDS/POI_PIN_VARIANT - логики пинов спасслужб,
// остающейся реактивным кодом компонента).
export interface BasemapLayersContext {
  t: (key: string) => string;
  lang: string;
  poiClusterExcludedKinds: string[];
}

const BASEMAP_BACKGROUND = "#f7f4ea";
// Подпись вершины - тот же графитовый цвет, что и у перевала.
const PEAK_LABEL_COLOR = "#3b3b3b";

// ── Landcover (покров земли) ─────────────────────────────────────────────
// Цвета заливки OpenTopoMap для четырёх значений `kind`, которые
// osm-import/flex-config/hiking.lua пишет в osm.landcover.
//
// Water и forest - буквальные значения OTM. Scrub и glacier подобраны в той
// же гамме: scrub - вегетационный зелёный, осветлённый к цвету бумаги
// (это ОТСУТСТВИЕ древесного покрова, должен читаться как тон, а не второй
// лес), glacier - водный синий, обесцвеченный почти до белого (так OTM рисует
// постоянный лёд, и так он выглядит).
//
// Два каменистых типа добавлены позже и решают реальную картографическую
// проблему: OpenTopoMap рисует осыпи и курумники крапом (рассеянные точки,
// без сплошной заливки), у MapLibre аналога нет без растрового паттерна.
// Сплошная заливка - честная замена, поэтому два типа различаются цветом, а
// не текстурой: scree теплее и светлее (мелкий рыхлый камень, ближе к цвету
// бумаги), blockfield темнее и холоднее (глыбы размером с машину - земля
// тяжелее, должна читаться тяжелее). Оба серо-бежевые, а не в зелёном или
// синем оттенке, чтобы осыпной склон нельзя было принять за растительность.
const LANDCOVER_COLOR: any = [
  "match",
  ["get", "kind"],
  "water", "#a3dde8",
  "forest", "#b9d984",
  "scrub", "#d5e7bb",
  "glacier", "#e3f0f4",
  "scree", "#ded7ca",
  "blockfield", "#cdc5b8",
  "transparent",
];

// Вода вынесена в отдельный слой НАД hillshade, растительность остаётся под
// ним. Рельефная штриховка корректна для леса (у склона есть освещённая и
// теневая сторона), но поверхность озера по определению плоская, и
// затемнение её hillshade выглядит как дыра в рельефе. Тот же источник, то
// же цветовое выражение, два слоя с разными фильтрами.
const LANDCOVER_IS_WATER: any = ["==", ["get", "kind"], "water"];

// ── Waterways (линейная вода) ────────────────────────────────────────────
// Вторая половина «воды», которой раньше не хватало вовсе: озеро - полигон
// в osm.landcover, а горный ручей слишком узок для площади и живёт только
// центральной линией. Без этого слоя долина с рекой посередине рисовалась
// как сухая земля.
//
// Оба цвета - тот же водный синий (#a3dde8) landcover, но темнее: линия
// должна оставаться различимой в один-два пикселя поверх той же самой
// заливки там, где река проходит через озеро. Река темнее и насыщеннее,
// ручей читается как её более бледный приток.
const RIVER_COLOR = "#4a9ec1";
const STREAM_COLOR = "#7bc0d6";
const WATERWAY_COLOR: any = ["match", ["get", "waterway"], "river", RIVER_COLOR, STREAM_COLOR];

const WATERWAY_WIDTH: any = [
  "interpolate",
  ["linear"],
  ["zoom"],
  8, waterwayWidthAt(0.8, 0.4),
  12, waterwayWidthAt(2, 1),
  15, waterwayWidthAt(3.6, 2),
  17, waterwayWidthAt(5, 2.8),
];

// `intermittent=yes` - русло сухое часть года, непредсказуемо какую именно.
// Рисуется прерывистой линией (стандартная картографическая конвенция) в
// отдельном слое, а не через data-driven `line-dasharray`: это свойство не
// data-driven в style spec MapLibre (в отличие от `line-width`), поэтому
// `match` на нём тихо откатывается к сплошной линии.
//
// В этом экстракте тег стоит у 7910 из 35726 ручьёв - это примерно каждый
// пятый, не крайний случай для такого сухого хребта, и рисовать их как
// постоянные значило бы врать о том, где вода есть.
const WATERWAY_IS_INTERMITTENT: any = ["==", ["get", "intermittent"], true];
const INTERMITTENT_DASH: [number, number] = [2.4, 1.8];

// Один цвет для всех троп, почти чёрный - так рисует OpenTopoMap. Раскраска
// по категории SAC (зелёный -> красный) была самодеятельностью, не
// конвенцией топокарт, и убрана. Сложность теперь несёт TRAIL_DASH -
// узор штриха, в отличие от оттенка, переживает печать, наложение на
// hillshade и чтение человеком с дальтонизмом.
const TRAIL_COLOR = "#1a1a1a";

// trail_visibility - второй полевой тег, стоящий отображения, естественно
// ложится на прозрачность: тропа с 'bad'/'horrible' реально хуже видна на
// местности, так что бледнее рисовать - честно, а не декоративно.
// Нетегированные линии (большинство) остаются полностью непрозрачными.
const TRAIL_OPACITY: any = [
  "match",
  ["get", "trail_visibility"],
  "excellent", 1,
  "good", 1,
  "intermediate", 0.85,
  "bad", 0.7,
  "horrible", 0.6,
  "no", 0.5,
  1,
];

// Ширина несёт второй, более грубый сигнал вместе с цветом: насколько это
// «настоящая» тропа. `footway` и `steps` тоньше `path`/`bridleway` в основном
// из-за того, что footway означает в городе - каждый тротуар Алматы это он,
// и на z10-12 они иначе сливаются в сплошное красно-коричневое пятно над
// городом, забивающее горные тропы. Утончение оставляет данные на карте, не
// давая им доминировать. (Отличить тротуар от лесной тропы нечем - подтег
// footway=sidewalk не входит в экспортируемые tile-функциями колонки, так
// что это намеренно грубый инструмент.)
const TRAIL_WIDTH: any = [
  "interpolate",
  ["linear"],
  ["zoom"],
  8, trailWidthAt(0.6, 0.3),
  12, trailWidthAt(1.3, 0.7),
  15, trailWidthAt(2.3, 1.6),
  17, trailWidthAt(3.2, 2.3),
];

// `amenity=shelter` покрывает две разные вещи, различить их можно только по
// `shelter_type`: убежище, где реально можно переночевать, и навес для
// пикника в городском парке. Смешивать их на горной карте хуже, чем не
// показывать вовсе - поэтому дневные навесы отодвинуты на высокий зум, а не
// убраны совсем (в парке на z14 они всё же полезны).
const SHELTER_IS_DAY_USE: any = [
  "match",
  ["get", "shelter_type"],
  ["picnic_shelter", "gazebo", "pavilion", "sun_shelter"],
  true,
  false,
];

// Зум, на котором каждый вид получает своё место. С восемнадцатью видами
// нужна плавная шкала, иначе гора либо тонет в урнах (1420 строк
// `waste_basket` внутри MAP_MAX_BOUNDS, почти все - уличная мебель Алматы),
// либо действительно редкие объекты (три `alpine_hut`, один `mountain_rescue`)
// ждут зума, на котором никто не планирует маршрут.
//
// Логика: объекты, вокруг которых планируют день, с исходного z9 карты,
// ресурсы, ради которых сделают крюк - на z12, лагерный комфорт - на z13,
// городская мебель - на z14, где уже смотрят на трейлхед, а не на хребет.
// С зума 13 подписи есть у всех видов (решение заказчика: «на 13 видно всё»).
const POI_MIN_ZOOM: any = [
  "case",
  SHELTER_IS_DAY_USE,
  13,
  [
    "match",
    ["get", "kind"],
    ["alpine_hut", "wilderness_hut", "rescue_hut", "camp_site", "mountain_rescue", "cable_car_station"], 9,
    ["spring", "drinking_water", "water_well", "water_tap", "water_point"], 12,
    ["shelter", "viewpoint", "ford"], 12,
    ["firepit", "toilets"], 13,
    ["waste_basket", "waste_disposal"], 13,
    12,
  ],
];

// Кто побеждает при коллизии подписей: спасслужба и хижина важнее стоянки,
// та важнее навеса/смотровой, та важнее воды, та важнее удобств. Меньшее
// число ставится первым.
const POI_LABEL_SORT_KEY: any = [
  "match",
  ["get", "kind"],
  ["mountain_rescue", "rescue_hut", "alpine_hut", "wilderness_hut", "cable_car_station"], 0,
  "camp_site", 1,
  ["viewpoint", "shelter"], 2,
  ["spring", "drinking_water", "water_well", "water_tap", "water_point"], 3,
  "ford", 4,
  ["firepit", "toilets"], 5,
  6,
];

// `interpolate` обязан быть верхнеуровневым выражением, а `case` -
// вложен как значение каждой ступени, не наоборот: MapLibre отклоняет
// zoom-выражение, вложенное внутрь другого выражения ("zoom expression
// may only be used as input to a top-level step or interpolate expression").
// Та же форма фикса, что и у text-size в places-label. Используется и
// admin-двойниками pois-peak/pois-pass (addPoiLayers), и buildings-label-admin
// (addLabelLayers) - вынесена на уровень модуля, а не в addPoiLayers, чтобы
// не дублировать между двумя функциями.
const editIconOpacity = (minzoom: number): any => [
  "interpolate", ["linear"], ["zoom"],
  minzoom, 0,
  minzoom + 1, ["case", ["==", ["get", "hidden"], true], 0.4, 1],
];

// Одни и те же DEM-тайлы объявлены дважды (см. HILLSHADE_SOURCE) -
// TERRAIN_SOURCE читает только setTerrain() (переключатель 3D, MapView.vue);
// слой hillshade читает HILLSHADE_SOURCE.
//
// MAP_MAX_BOUNDS (74.80-79.80E, 41.80-44.30N) и покрытие dem_tiles
// (SRTM N41E074..N44E079, 24 квадрата) теперь связаны: вся достижимая
// область лежит внутри этих квадратов, поэтому края с видимой прямой
// границей штриховки нет. Расширение MAP_MAX_BOUNDS за их пределы вернёт
// артефакт и потребует расширить dem_tiles (fetch_dem_tiles.py) и
// перегенерировать contours (generate_contours.py, DEFAULT_BBOX) тем же
// изменением.
//
// Отдельная ловушка: серый прямоугольник с вертикальными полосами может
// быть не пробелом DEM, а отравленным кэшем. /terrain-tiles/ отдаётся с
// `max-age=30d, immutable` и без версии в URL, поэтому тайл, сгенерированный
// в момент, когда бэкенд не мог прочитать dem_tiles, застынет на месяц у
// каждого клиента, который его получил. Если штриховка пропала на
// прямоугольнике - сначала сверить байты тайла с бэкендом на :8001
// напрямую, не списывать на покрытие (см. known-issues.md).
//
// Атрибуция: один и тот же ODbL-кредит на всех векторных источниках.
// AttributionControl MapLibre дедуплицирует одинаковые строки, показывается
// один раз.
const addSources = (m: maplibregl.Map, clusterGlyphs: PoiClusterGlyph[]) => {
  const demSourceSpec = {
    type: "raster-dem" as const,
    tiles: [terrainUrlBase],
    tileSize: 256,
    encoding: "mapbox" as const,
    maxzoom: 12,
  };
  m.addSource(TERRAIN_SOURCE, demSourceSpec);
  m.addSource(HILLSHADE_SOURCE, demSourceSpec);

  const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  // z8 совпадает с нижней границей landcover_tile (Alembic 20260825_04: ниже
  // z8 после фильтра по площади остаются только гигантские мультиполигоны,
  // упрощение которых стоит секунд на тайл). На два уровня ниже собственного
  // minZoom карты (10), так что ничего недостижимого. maxzoom 14, а не 15 -
  // заливка ничего не выигрывает от последнего уровня, overzoom на один
  // уровень незаметен.
  m.addSource(LANDCOVER_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(LANDCOVER_SOURCE)],
    minzoom: 8,
    maxzoom: 14,
    attribution: OSM_ATTRIBUTION,
  });
  // Та же нижняя граница z8, что и у landcover, и по той же причине - это
  // линейная половина той же воды. Сервер отдаёт реки только ниже z12
  // (waterways_tile), так что на низких зумах видна структура русел, а не пятно.
  m.addSource(WATERWAYS_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(WATERWAYS_SOURCE)],
    minzoom: 8,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  m.addSource(ROADS_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(ROADS_SOURCE)],
    minzoom: 4,
    // 15, как trails/waterways, а не 17 - роуды overzoom-ятся так же, как и
    // любые векторные тайлы, свежий фетч выше зума, на котором trails уже
    // перестают обновляться, ничего не даёт.
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  m.addSource(TRAILS_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(TRAILS_SOURCE)],
    minzoom: 8,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  m.addSource(POIS_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(POIS_SOURCE)],
    minzoom: 9,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  // Admin-двойник (см. комментарий у POIS_ADMIN_SOURCE в constants.ts). Не
  // используется, пока не включён editMode.
  m.addSource(POIS_ADMIN_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(POIS_ADMIN_SOURCE)],
    minzoom: 9,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  // Стартует пустым - refreshPoiClusterSource (MapView.vue) заполняет его,
  // когда тайлы POIS_SOURCE реально загрузятся (слушатель 'idle').
  // clusterMaxZoom 16, а не дефолт supercluster (14): источник держит только
  // точки, уже видимые на экране, поэтому два глифа в нескольких метрах друг
  // от друга не должны оставаться слитыми в бейдж, когда зум уже позволяет
  // разместить их отдельно.
  //
  // clusterRadius 30, не дефолтные 50 - при 50px два кружка (13-28px, см.
  // pois-icon-unclustered-halo) оставались слитыми в бейдж далеко за той
  // точкой, где между ними уже было явное расстояние, и кластеризация
  // читалась как «неохотно отпускает», а не честный сигнал плотности. 30px
  // ближе к «эти кружки реально пересеклись бы или столпились».
  m.addSource(POIS_CLUSTER_SOURCE, {
    type: "geojson",
    data: EMPTY_FEATURES,
    cluster: true,
    clusterRadius: 30,
    clusterMaxZoom: 16,
    clusterProperties: buildPoiClusterProperties(clusterGlyphs),
  });
  // Изолинии - не OSM-данные, они генерируются из тех же растров SRTM, что и
  // 3D-рельеф (dem_tiles -> backend/scripts/generate_contours.py), поэтому у
  // них собственный кредит, а не ODbL сверху.
  m.addSource(CONTOURS_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(CONTOURS_SOURCE)],
    minzoom: 11,
    maxzoom: 15,
    attribution: "Contours: NASA SRTM",
  });
  m.addSource(CLIFFS_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(CLIFFS_SOURCE)],
    minzoom: 12,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  // buildings-fill/buildings-dam ниже дают клиентский minzoom 14, на уровень
  // выше собственного порога источника (buildings_tile - от 12, Alembic
  // 20260828_02), так что тайл уже в кэше к моменту, когда слой начинает
  // рисовать, а не запрашивается «холодным» на первом кадре.
  m.addSource(BUILDINGS_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(BUILDINGS_SOURCE)],
    minzoom: 12,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  // Admin-двойник (BUILDINGS_ADMIN_SOURCE). Не используется, пока не включён editMode.
  m.addSource(BUILDINGS_ADMIN_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(BUILDINGS_ADMIN_SOURCE)],
    minzoom: 12,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  m.addSource(AERIALWAYS_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(AERIALWAYS_SOURCE)],
    minzoom: 10,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  m.addSource(RIDGES_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(RIDGES_SOURCE)],
    minzoom: 12,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  m.addSource(POWER_LINES_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(POWER_LINES_SOURCE)],
    minzoom: 10,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  // Порог на два уровня ниже собственного minZoom карты (10) - тот же запас,
  // что и у landcover_tile: граница парка или города - ориентир, нужный уже
  // на виде всего хребта, ждать его незачем.
  m.addSource(BOUNDARIES_SOURCE, {
    type: "vector",
    // ?v=: тайлы границ кешируются браузером как immutable на месяц - при смене
    // содержимого (20261019_01: label_lon/label_lat, фильтр) версия сбрасывает кеш.
    tiles: [`${vectorTileUrl(BOUNDARIES_SOURCE)}?v=20261019`],
    minzoom: 8,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
  m.addSource(PLACES_SOURCE, {
    type: "vector",
    tiles: [vectorTileUrl(PLACES_SOURCE)],
    minzoom: 8,
    maxzoom: 15,
    attribution: OSM_ATTRIBUTION,
  });
};

// Покров земли первым - единственное на этой карте, что поверхность, а не
// отметка, всё остальное рисуется поверх него. Рельефная штриховка (hillshade)
// - между заливками растительности и водой; край леса/кустарника - поверх
// hillshade, чтобы штриховка не гасила его в нечитаемость.
const addLandcoverAndHillshadeLayers = (m: maplibregl.Map) => {
  m.addLayer({
    id: "basemap-background",
    type: "background",
    paint: { "background-color": BASEMAP_BACKGROUND },
  });

  m.addLayer({
    // Всё, что не вода: растительность и голая скала (scree/blockfield).
    // Отсюда "ground", а не "vegetation".
    id: "landcover-ground",
    type: "fill",
    source: LANDCOVER_SOURCE,
    "source-layer": LANDCOVER_SOURCE,
    filter: ["!", LANDCOVER_IS_WATER],
    paint: {
      "fill-color": LANDCOVER_COLOR,
      // Не полностью непрозрачно: эти заливки лежат под hillshade, и
      // проступающий сквозь них тон бумаги оставляет лесистый склон лесом на
      // рельефе, а не плоской зелёной вырезкой. Ещё сдержаннее на собственном
      // minZoom карты, где виден весь хребет и сплошная зелень читалась бы
      // картой землепользования.
      "fill-opacity": ["interpolate", ["linear"], ["zoom"], 10, 0.78, 12, 0.9],
      "fill-antialias": true,
    },
  });

  // Все цвета здесь намеренно полупрозрачны. Непрозрачный блик выбеливает
  // цвет бумаги в плоский серый и забирает с собой тон landcover; на этих
  // альфах штриховка читается как падающий на карту свет, а не как слой
  // поверх неё. Тень тонирована в зелёно-серый, а не нейтральный, по той же
  // причине - чисто чёрная тень поверх #b9d984 обесцвечивает его до оливкового.
  m.addLayer({
    id: "hillshade",
    type: "hillshade",
    source: HILLSHADE_SOURCE,
    // Ниже z9 собственный maxzoom-12 DEM-тайлов даунсэмплится так, что
    // штриховка превращается в кашу, а весь этот диапазон и так выше
    // полезного масштаба для карты одного хребта.
    minzoom: 9,
    paint: {
      "hillshade-exaggeration": 0.5,
      "hillshade-shadow-color": "rgba(72, 86, 78, 0.55)",
      "hillshade-highlight-color": "rgba(255, 255, 255, 0.35)",
      "hillshade-accent-color": "rgba(120, 136, 126, 0.30)",
    },
  });

  // Конвенция OpenTopoMap для «это растительность, а не зона землепользования».
  // Вода/ледник/осыпь/курумник намеренно исключены: край озера уже задан
  // границей заливки waterway/landcover-water, а осыпь читается как голая
  // скала, которую и сама OpenTopoMap не обводит.
  m.addLayer({
    id: "landcover-ground-outline",
    type: "line",
    source: LANDCOVER_SOURCE,
    "source-layer": LANDCOVER_SOURCE,
    filter: ["match", ["get", "kind"], ["forest", "scrub"], true, false],
    minzoom: 11,
    layout: { "line-join": "round" },
    paint: {
      "line-color": "#7fa855",
      "line-width": ["interpolate", ["linear"], ["zoom"], 11, 0.6, 15, 1.1, 17, 1.4],
      "line-dasharray": [1, 2],
      "line-opacity": ["interpolate", ["linear"], ["zoom"], 11, 0.5, 13, 0.85],
    },
  });
};

export const PROTECTED_LABEL_SOURCE = "protected-area-labels";
// Ниже этого зума нацпарк показан заливкой с подписью, выше - только границей.
export const PROTECTED_FAR_MAX_ZOOM = 12;
const PROTECTED_AREA_COLOR = "#d0382b";
const PROTECTED_AREA_LABEL_COLOR = "#8f1f15";

// Границы охраняемых территорий/административные, водные полигоны и линии.
const addBoundariesAndWaterwayLayers = (m: maplibregl.Map, ctx: BasemapLayersContext) => {
  // Охраняемые территории (нацпарки, заповедники; osm.boundaries) - слой меню
  // «Слои» «Нацпарки и заповедники», скрыт по умолчанию (видимость
  // переключает MapView.vue::applyProtectedAreas). Два режима по масштабу, граница
  // между ними - тот же зум, с которого рисуются POI (POI_DETAIL_MIN_ZOOM):
  //  - далеко (до PROTECTED_FAR_MAX_ZOOM): заливка + граница + подпись с названием -
  //    видно, где парк;
  //  - ближе: только сплошная красная граница, без заливки, чтобы не закрашивать
  //    карту под точками и маршрутами; на зуме POI она толстая.
  const protectedFilter: maplibregl.FilterSpecification = ["==", ["get", "kind"], "protected_area"];
  m.addLayer({
    id: "boundaries-fill",
    type: "fill",
    source: BOUNDARIES_SOURCE,
    "source-layer": BOUNDARIES_SOURCE,
    minzoom: 6,
    maxzoom: PROTECTED_FAR_MAX_ZOOM,
    filter: protectedFilter,
    layout: { visibility: "none" },
    paint: {
      "fill-color": PROTECTED_AREA_COLOR,
      // Заливка только издалека и сходит на нет к зуму смены режима.
      "fill-opacity": ["interpolate", ["linear"], ["zoom"], 8, 0.13, 10, 0.1, PROTECTED_FAR_MAX_ZOOM, 0],
    },
  });
  m.addLayer({
    id: "boundaries-line",
    type: "line",
    source: BOUNDARIES_SOURCE,
    "source-layer": BOUNDARIES_SOURCE,
    minzoom: 6,
    maxzoom: PROTECTED_FAR_MAX_ZOOM,
    filter: protectedFilter,
    layout: { "line-join": "round", visibility: "none" },
    paint: {
      "line-color": PROTECTED_AREA_COLOR,
      "line-width": ["interpolate", ["linear"], ["zoom"], 6, 1.4, PROTECTED_FAR_MAX_ZOOM, 2.8],
      "line-opacity": 0.85,
    },
  });
  m.addLayer({
    id: "boundaries-line-near",
    type: "line",
    source: BOUNDARIES_SOURCE,
    "source-layer": BOUNDARIES_SOURCE,
    minzoom: PROTECTED_FAR_MAX_ZOOM,
    filter: protectedFilter,
    layout: { "line-join": "round", "line-cap": "round", visibility: "none" },
    paint: {
      "line-color": PROTECTED_AREA_COLOR,
      "line-width": ["interpolate", ["linear"], ["zoom"], PROTECTED_FAR_MAX_ZOOM, 2.8, POI_DETAIL_MIN_ZOOM, 4.2, 18, 6.5],
      "line-opacity": 0.95,
    },
  });
  // Подпись - из отдельного GeoJSON-источника с одной точкой на парк (его
  // наполняет MapView.vue::refreshProtectedAreaLabels): подпись из тайлов
  // дублировалась бы на каждом куске парка, разрезанного по тайлам.
  m.addSource(PROTECTED_LABEL_SOURCE, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  m.addLayer({
    id: "boundaries-label",
    type: "symbol",
    source: PROTECTED_LABEL_SOURCE,
    maxzoom: PROTECTED_FAR_MAX_ZOOM,
    layout: {
      visibility: "none",
      "text-field": buildNameField(ctx.lang),
      "text-font": MAP_FONT,
      "text-size": ["interpolate", ["linear"], ["zoom"], 7, 11, 12, 15],
      "text-max-width": 10,
      "text-letter-spacing": 0.04,
      "text-padding": 6,
    },
    paint: {
      "text-color": PROTECTED_AREA_LABEL_COLOR,
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.6,
    },
  });

  m.addLayer({
    id: "landcover-water",
    type: "fill",
    source: LANDCOVER_SOURCE,
    "source-layer": LANDCOVER_SOURCE,
    filter: LANDCOVER_IS_WATER,
    paint: {
      "fill-color": LANDCOVER_COLOR,
      // Более плоско и непрозрачно, чем растительность под ним: у воды
      // действительно нет текстуры, и это ориентир для навигации, поэтому
      // почти сплошной цвет на любом зуме, без затухания.
      "fill-opacity": 0.95,
      "fill-antialias": true,
    },
  });

  // Линейная вода прямо над своими водными полигонами - река, расширяющаяся
  // в озеро, должна уходить В заливку, а не обрываться на её краю. Над
  // hillshade по той же причине, что и полигоны: затенение русла делает его
  // похожим на складку рельефа.
  //
  // Сначала постоянные, прерывистые поверх - там, где сезонное русло
  // ответвляется от постоянного, прерывистая линия информативнее и не
  // должна теряться под сплошной.
  m.addLayer({
    id: "waterways-line",
    type: "line",
    source: WATERWAYS_SOURCE,
    "source-layer": WATERWAYS_SOURCE,
    filter: ["!", WATERWAY_IS_INTERMITTENT],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": WATERWAY_COLOR,
      "line-width": WATERWAY_WIDTH,
    },
  });
  m.addLayer({
    id: "waterways-line-intermittent",
    type: "line",
    source: WATERWAYS_SOURCE,
    "source-layer": WATERWAYS_SOURCE,
    filter: WATERWAY_IS_INTERMITTENT,
    // "butt", не "round": скруглённый конец на пунктирной линии закругляет
    // каждый штрих и превращает узор разрывов в ряд ромбов - та же проблема,
    // что делала пунктир троп нечитаемым (см. trails-line).
    layout: { "line-cap": "butt", "line-join": "round" },
    paint: {
      "line-color": WATERWAY_COLOR,
      "line-width": WATERWAY_WIDTH,
      "line-dasharray": INTERMITTENT_DASH,
      // Бледнее, а не только прерывисто - на z10 узор штриха уже меньше
      // пикселя, и только прозрачность продолжает нести сигнал.
      "line-opacity": 0.75,
    },
  });
};

// Изолинии дальше, в основании нарисованного стека - это сам рельеф, всё
// остальное лежит на нём.
const addContourLayers = (m: maplibregl.Map) => {
  // Два слоя, а не один, потому что опорная линия - не «та же линия, но
  // толще», она темнее И толще, и разделение позволяет фильтровать
  // промежуточные линии на низком зуме, не трогая опорные.
  m.addLayer({
    id: "contours-line",
    type: "line",
    source: CONTOURS_SOURCE,
    "source-layer": CONTOURS_SOURCE,
    minzoom: 11,
    filter: ["!", CONTOUR_IS_INDEX],
    layout: { "line-join": "round", "line-cap": "round" },
    paint: {
      "line-color": CONTOUR_COLOR,
      // Шкала OTM от волосяной линии до 0.6px. Субпиксельная ширина не
      // ошибка: на HiDPI-экране линия 0.2px - это лёгкий тон, а не штрих,
      // ровно то, чем и должна быть промежуточная изолиния на зуме, где она
      // впервые появляется.
      "line-width": ["interpolate", ["linear"], ["zoom"], 11, 0.2, 13, 0.35, 14, 0.5, 17, 0.6],
      // Изолинии проявляются плавно, а не включаются - чтобы момент, когда
      // набор 20 м сменяет набор 50 м (z13, contours_tile), не читался
      // вспышкой новых линий.
      "line-opacity": ["interpolate", ["linear"], ["zoom"], 11, 0.45, 13, 0.8, 15, 0.9],
    },
  });
  m.addLayer({
    id: "contours-index",
    type: "line",
    source: CONTOURS_SOURCE,
    "source-layer": CONTOURS_SOURCE,
    minzoom: 11,
    filter: CONTOUR_IS_INDEX,
    layout: { "line-join": "round", "line-cap": "round" },
    paint: {
      "line-color": CONTOUR_COLOR,
      "line-width": ["interpolate", ["linear"], ["zoom"], 11, 0.4, 13, 0.7, 14, 1, 17, 1.2],
      "line-opacity": ["interpolate", ["linear"], ["zoom"], 11, 0.6, 13, 0.9, 15, 1],
    },
  });

  // Числа высот только на опорных (100 м) изолиниях и только когда линии
  // достаточно далеко друг от друга, чтобы число поместилось.
  // symbol-placement "line" с большим symbol-spacing вставляет число ПРЯМО В
  // линию, как на бумажной карте; MapLibre не делает разрыв в линии под
  // текстом, эту работу вместо этого делает ореол - потому он в цвет фона, а не белый.
  m.addLayer({
    id: "contours-label",
    type: "symbol",
    source: CONTOURS_SOURCE,
    "source-layer": CONTOURS_SOURCE,
    minzoom: 13,
    filter: CONTOUR_IS_INDEX,
    layout: {
      "text-field": ["to-string", ["get", "elevation_m"]],
      "text-font": MAP_FONT,
      "text-size": ["interpolate", ["linear"], ["zoom"], 13, 9, 16, 11],
      "symbol-placement": "line",
      "symbol-spacing": 320,
      "text-max-angle": 25,
      "text-padding": 6,
      // Число читается вдоль склона, не вверх ногами на дальней стороне гребня.
      "text-rotation-alignment": "map",
      "text-pitch-alignment": "viewport",
    },
    paint: {
      "text-color": CONTOUR_LABEL_COLOR,
      "text-halo-color": BASEMAP_BACKGROUND,
      "text-halo-width": 1.6,
    },
  });
};

// Скалы сгруппированы с изолиниями, а не с дорогами/тропами - и то, и
// другое рельефная опасность, а не инфраструктура. Гребни/арэты - следом,
// та же коричневая гамма, что у CONTOUR_LABEL_COLOR.
const addCliffsAndRidgeLayers = (m: maplibregl.Map) => {
  // pixelRatio 1 (не 2, как у иконок POI): buildCliffHachureImage рисует в
  // точном размере, в котором line-pattern должен его тайлить, удвоение
  // сдвинуло бы и шаг штрихов на экране.
  if (!m.hasImage(CLIFF_PATTERN_IMAGE_ID)) {
    const cliffImage = buildCliffHachureImage();
    if (cliffImage) m.addImage(CLIFF_PATTERN_IMAGE_ID, cliffImage, { pixelRatio: 1 });
  }
  m.addLayer({
    id: "cliffs-line",
    type: "line",
    source: CLIFFS_SOURCE,
    "source-layer": CLIFFS_SOURCE,
    minzoom: 12,
    layout: { "line-cap": "butt", "line-join": "round" },
    paint: {
      "line-pattern": CLIFF_PATTERN_IMAGE_ID,
      // Управляет высотой рендера тайла - насколько зубец выступает от
      // линии, а не толщиной штриха, как у остальных line-слоёв здесь.
      "line-width": ["interpolate", ["linear"], ["zoom"], 12, 6, 15, 10, 17, 13],
      // Смягчает штриховку, чтобы не читалась сплошной чёрной полосой; заодно
      // служит плавным появлением слоя (0.82 - потолок, не фиксированное
      // значение, см. fadeInFrom).
      "line-opacity": fadeInFrom(12, 0.82),
    },
  });

  // Гребень читает форму рельефа, а не опасность, поэтому обычная линия, а
  // не паттерн, как у cliffs-line.
  m.addLayer({
    id: "ridges-line",
    type: "line",
    source: RIDGES_SOURCE,
    "source-layer": RIDGES_SOURCE,
    minzoom: 12,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#a06a45",
      "line-width": ["interpolate", ["linear"], ["zoom"], 12, 1, 15, 1.6, 17, 2.2],
      "line-opacity": fadeInFrom(12, 0.7),
    },
  });
};

// Здания и дамбы (osm.buildings, различаются по kind в hiking.lua).
// Сгруппированы между рельефными/опасными слоями выше и инфраструктурой
// ниже: контур здания - это то, на чём лежат дороги/тропы/иконки POI,
// а не то, вокруг чего они визуально огибают.
const addBuildingLayers = (m: maplibregl.Map) => {
  // Две заливки на один общий слой контура, а не три отдельных - обводка
  // (кроме цвета) одинакова для обоих видов, дамба визуально просто более
  // тяжёлое здание, если смотреть на линию вокруг.
  const BUILDING_IS_DAM: any = ["==", ["get", "kind"], "dam"];
  m.addLayer({
    id: "buildings-fill",
    type: "fill",
    source: BUILDINGS_SOURCE,
    "source-layer": BUILDINGS_SOURCE,
    filter: ["!", BUILDING_IS_DAM],
    minzoom: 14,
    paint: { "fill-color": "#cbbfae", "fill-opacity": fadeInFrom(14, 0.85) },
  });
  // У дамб свой цвет (сталистее серый, читается как бетон/инженерное
  // сооружение, а не крыша) и более низкий minzoom, чем у обычных зданий -
  // дамба Медеу реально крупное, важное для ориентирования сооружение.
  m.addLayer({
    id: "buildings-dam",
    type: "fill",
    source: BUILDINGS_SOURCE,
    "source-layer": BUILDINGS_SOURCE,
    filter: BUILDING_IS_DAM,
    minzoom: 12,
    paint: { "fill-color": "#9aa3ab", "fill-opacity": fadeInFrom(12, 0.9) },
  });
  // Два слоя контура, не один общий `minzoom: 12` - иначе контуры обычных
  // зданий рисовались бы на два уровня зума раньше, чем buildings-fill (z14)
  // хоть что-то в них закрашивает - сетка пустых прямоугольников. Каждый
  // теперь точно совпадает с minzoom/fade своей заливки.
  m.addLayer({
    id: "buildings-outline-dam",
    type: "line",
    source: BUILDINGS_SOURCE,
    "source-layer": BUILDINGS_SOURCE,
    filter: BUILDING_IS_DAM,
    minzoom: 12,
    layout: { "line-join": "round" },
    paint: { "line-color": "#4a535a", "line-width": 1.4, "line-opacity": fadeInFrom(12, 1) },
  });
  m.addLayer({
    id: "buildings-outline",
    type: "line",
    source: BUILDINGS_SOURCE,
    "source-layer": BUILDINGS_SOURCE,
    filter: ["!", BUILDING_IS_DAM],
    minzoom: 14,
    layout: { "line-join": "round" },
    paint: { "line-color": "#7d6f5c", "line-width": 0.7, "line-opacity": fadeInFrom(14, 1) },
  });
};

// Дороги, канатки/гондолы/кресельки, воздушные ЛЭП - контурная инфраструктура.
const addRoadsAerialwaysPowerLineLayers = (m: maplibregl.Map) => {
  // Дороги. Разделены так же, как на OpenTopoMap (и на любой бумажной
  // топокарте): чёрная обводка + белая заливка для транспортных с покрытием
  // (motorway..residential); trails-line ниже держит тонкую пунктирную линию
  // для footway/path/bridleway/steps, куда track не входит (см. комментарий
  // road_classes в hiking.lua).
  //
  // `track` делит ту же бело-заливочную форму, что и обычная дорога с
  // покрытием (грейдерная грунтовка на местности читается так же, когда
  // турист решает, идти ли по ней), но обводка у неё пунктирная, а не
  // сплошная - единственное, что всё ещё маркирует её как неасфальтированную.
  // `roads-line` (белая заливка) общая для всех классов включая track;
  // разделяется только обводка, так как `line-dasharray` не data-driven
  // свойство в MapLibre (то же ограничение, что у TRAIL_DASH/WATERWAY) -
  // отдельный пунктирный слой обводки для track, отдельный сплошной для
  // всего остального, оба под одной общей заливкой.
  const ROAD_IS_TRACK: any = ["==", ["get", "highway"], "track"];
  const pavedWidthAt = (motorwayTrunk: number, primary: number, other: number): any => [
    "match",
    ["get", "highway"],
    ["motorway", "trunk"], motorwayTrunk,
    "primary", primary,
    other,
  ];
  const ROAD_FILL_WIDTH: any = [
    "interpolate", ["linear"], ["zoom"],
    4, pavedWidthAt(0.9, 0.6, 0.4),
    9, pavedWidthAt(2, 1.5, 1),
    12, pavedWidthAt(3.5, 2.6, 1.8),
    15, pavedWidthAt(5, 4, 2.8),
    17, pavedWidthAt(6.5, 5.2, 3.6),
  ];
  // На каждой ступени достаточно шире, чтобы оставался видимый чёрный
  // ободок вокруг белой заливки - не просто «ширина заливки плюс константа»,
  // которая исчезала бы на низком зуме и забивала заливку на высоком.
  const ROAD_CASING_WIDTH: any = [
    "interpolate", ["linear"], ["zoom"],
    4, pavedWidthAt(1.5, 1.1, 0.8),
    9, pavedWidthAt(3.2, 2.5, 1.8),
    12, pavedWidthAt(5, 4, 3),
    15, pavedWidthAt(7, 5.8, 4.2),
    17, pavedWidthAt(9, 7.4, 5.4),
  ];

  // Пунктирная обводка, только track - butt-концы/соединения (не
  // скруглённые, как у остальных линий этой карты) по той же причине, что
  // и у TRAIL_DASH: скругление превращает каждый штрих в ромб.
  m.addLayer({
    id: "roads-casing-track",
    type: "line",
    source: ROADS_SOURCE,
    "source-layer": ROADS_SOURCE,
    filter: ROAD_IS_TRACK,
    layout: { "line-cap": "butt", "line-join": "round" },
    paint: {
      "line-color": "#4a4640",
      "line-width": ROAD_CASING_WIDTH,
      "line-dasharray": [1.6, 1.3],
    },
  });
  m.addLayer({
    id: "roads-casing",
    type: "line",
    source: ROADS_SOURCE,
    "source-layer": ROADS_SOURCE,
    filter: ["!", ROAD_IS_TRACK],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": "#4a4640", "line-width": ROAD_CASING_WIDTH },
  });
  // Общая для всех классов, включая track - сама заливка не различает
  // асфальт и грунт, это делает только обводка выше.
  m.addLayer({
    id: "roads-line",
    type: "line",
    source: ROADS_SOURCE,
    "source-layer": ROADS_SOURCE,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": "#ffffff", "line-width": ROAD_FILL_WIDTH },
  });

  // Канатки/гондолы/кресельки (osm.aerialways) - линия с насечками. Добавлена
  // ПОСЛЕ дорог, а не вместе с ними: коридор канатки часто идёт параллельно
  // дороге или пересекает её, и при рисовании раньше дорог непрозрачная белая
  // заливка дороги закрашивала трос, оставляя видимыми только насечки,
  // торчащие за ширину дороги - реальный баг, найденный на асфальтированном
  // подъезде прямо под гондолой Шымбулака. Канатка - более заметный и менее
  // заменимый объект из двух, поэтому её порядок отрисовки выше.
  if (!m.hasImage(AERIALWAY_PATTERN_IMAGE_ID)) {
    const tickImage = buildAerialwayTickImage();
    if (tickImage) m.addImage(AERIALWAY_PATTERN_IMAGE_ID, tickImage, { pixelRatio: 1 });
  }
  m.addLayer({
    id: "aerialways-line",
    type: "line",
    source: AERIALWAYS_SOURCE,
    "source-layer": AERIALWAYS_SOURCE,
    minzoom: 10,
    layout: { "line-cap": "butt", "line-join": "round" },
    paint: {
      "line-pattern": AERIALWAY_PATTERN_IMAGE_ID,
      // Управляет высотой рендера тайла - насколько насечка выступает от
      // троса, а не толщиной штриха, как везде ещё.
      "line-width": ["interpolate", ["linear"], ["zoom"], 10, 4, 14, 7, 17, 9.5],
      "line-opacity": fadeInFrom(10, 1),
    },
  });

  // Воздушные ЛЭП (osm.power_lines) - тонкая обычная пунктирная линия, а не
  // паттерн: в отличие от канатки или скалы, визуальный образ ЛЭП на любой
  // референсной карте (включая OSM Carto) - просто «тонкая тёмная линия,
  // ровный пунктир», без перпендикулярных насечек.
  //
  // `kind` (power=line против minor_line, уже несётся тайлом) определяет
  // толщину/прозрачность: магистральная линия - реальный ориентир издалека
  // (широкая просека, видна за километры), локальная распределительная -
  // нет, поэтому она тоньше и проявляется позже, не конкурируя на широком зуме.
  m.addLayer({
    id: "power-lines-line",
    type: "line",
    source: POWER_LINES_SOURCE,
    "source-layer": POWER_LINES_SOURCE,
    minzoom: 10,
    layout: { "line-cap": "butt", "line-join": "round" },
    paint: {
      "line-color": "#8a8378",
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        10,
        ["match", ["get", "kind"], "line", 0.9, 0.5],
        14,
        ["match", ["get", "kind"], "line", 1.6, 0.9],
        17,
        ["match", ["get", "kind"], "line", 2.4, 1.4],
      ],
      "line-dasharray": [3, 2],
      "line-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        10,
        ["match", ["get", "kind"], "line", 0.85, 0],
        11,
        ["match", ["get", "kind"], "line", 0.85, 0.6],
      ],
    },
  });
};

// Один слой троп, не два - `line-color`/`line-width` всё равно ветвятся по
// `highway` через выражения. Сюда попадает всё: OSM footway/path/bridleway/
// steps/track, плюс public.hiking_trails (там `highway` NULL).
const addTrailLayers = (m: maplibregl.Map) => {
  // line-cap "butt": скруглённый конец на пунктирной линии закругляет
  // каждый штрих, при такой толщине превращая ровный пунктир в ряд ромбов и
  // полностью теряя разницу между тремя узорами.
  // LOD: скрыто ниже z12.5, а не рисуется с собственной нижней границы
  // источника z8. На широком зуме это было 80K+ сегментов тонких линий по
  // всему хребту сразу - contours/landcover/peaks и так ориентируют на этом
  // масштабе, а реальная сеть троп нужна только когда уже приближен к
  // конкретной долине или трейлхеду. trails_tile не менялся (всё ещё отдаёт
  // с z8) - это чисто клиентский порог показа, не срез данных, так что
  // изменение порога не требует переимпорта или сброса кэша тайлов.
  m.addLayer({
    id: "trails-line",
    type: "line",
    source: TRAILS_SOURCE,
    "source-layer": TRAILS_SOURCE,
    minzoom: 12.5,
    layout: { "line-cap": "butt", "line-join": "round" },
    paint: {
      "line-color": TRAIL_COLOR,
      "line-opacity": TRAIL_OPACITY,
      "line-width": TRAIL_WIDTH,
      "line-dasharray": TRAIL_DASH,
    },
  });

  // Подписи имён троп (trails-label) убраны: серпантинные горные тропы
  // забивали карту длинными казахскими названиями. Сейчас имя/sac_scale
  // тропы на карте вообще не показывается.
};

// POI поверх линий, чтобы вершина или хижина никогда не терялась под
// тропой - и теперь поверх изолиний, которые пересекают каждую из них.
const addPoiLayers = (m: maplibregl.Map, ctx: BasemapLayersContext, clusterGlyphs: PoiClusterGlyph[]) => {
  // Вершины первыми, треугольником (buildPeakIconImage) - единственный вид
  // POI со своей отметкой, остальные делят точку ниже.
  //
  // Треугольник и подпись - ОДИН symbol-слой, а не symbol-слой плюс ветка
  // общего pois-label ниже. MapLibre разрешает коллизии по всем symbol-слоям
  // сразу, сверху вниз, и если подпись жила в более высоком слое, она
  // размещалась первой, а треугольнику под ней места уже не доставалось:
  // на z12 из 77 видимых вершин получилось 52 подписи, но только 5
  // треугольников - имена вершин висели без самой отметки. Объединение
  // иконки и текста в один символ делает их одним решением о размещении,
  // как и задумывалось изначально.
  //
  // Слой остаётся НИЖЕ pois-label - по тому же правилу «сверху первым»
  // хижины, стоянки и родники получают место первыми, вершины заполняют
  // остальное. Так и надо: 872 вершины внутри границ против нескольких
  // сотен всего остального - размести вершины первыми, и редкие объекты,
  // вокруг которых реально планируют маршрут, потерялись бы.
  const addPoiIcon = (id: string, image: ImageData | null, sdf = false) => {
    if (image && !m.hasImage(id)) {
      m.addImage(id, image, { pixelRatio: 2, sdf });
    }
  };
  addPoiIcon(PEAK_ICON_IMAGE_ID, buildPeakIconImage("light"));
  addPoiIcon(PEAK_ICON_DARK_IMAGE_ID, buildPeakIconImage("dark"));
  addPoiIcon(PASS_ICON_IMAGE_ID, buildPassIconImage());
  for (const [kind, { viewBox, paths, strokeWidth, originX, originY, fillRule }] of Object.entries(POI_ICON_SVG_PATHS)) {
    addPoiIcon(
      poiIconImageId(kind),
      buildSvgPathIconImage(viewBox, paths, strokeWidth, originX, originY, fillRule),
      true,
    );
  }
  m.addLayer({
    id: "pois-peak",
    type: "symbol",
    source: POIS_SOURCE,
    "source-layer": POIS_SOURCE,
    // pois_tile всегда несёт `pinned` (COALESCE(ov.pinned, false)),
    // закреплённая вершина/перевал исключается здесь и вместо этого
    // рендерится пином (см. фильтр PIN_POI_KINDS-or-pinned в
    // refreshPoiPinMarkers) - объект не должен рисоваться дважды.
    filter: ["all", ["==", ["get", "kind"], "peak"], ["!=", ["get", "pinned"], true]],
    // LOD: порог совпадает с новым порогом trails-line - ниже него отметка
    // вершины несёт мало действенного, а прореженная коллизией выборка на
    // z11-12 всё ещё была достаточно плотной, вместе с исчезающими на этом
    // же диапазоне тропами читалась как «карта состоит из одних вершин»,
    // а не обзор рельефа. Реальное прореживание всё ещё делает шкала
    // padding ниже - это только сдвигает точку старта.
    minzoom: 12.5,
    layout: {
      "icon-image": PEAK_ICON_IMAGE_ID,
      "icon-size": ["interpolate", ["linear"], ["zoom"], 12.5, 0.8, 13, 0.95, 15, 1.25],
      // Отметка вершины закреплена на позиции на земле, не декаль поверх
      // рельефа: должна оставаться вертикальной и без масштабирования при
      // наклоне 3D-вида, та же логика, что и у circle-pitch-alignment ниже.
      "icon-pitch-alignment": "viewport",
      "icon-rotation-alignment": "viewport",
      // Намеренно НЕ icon-allow-overlap: 872 вершины внутри MAP_MAX_BOUNDS,
      // рисуй их безусловно - и хребет превращается в серый шум даже на
      // этом более узком диапазоне зума. Движок коллизий прореживает их -
      // но только настолько, насколько ему сказано, а треугольник ~7 CSS px
      // почти ни с чем не сталкивается, так что реальное прореживание
      // делает шкала padding ниже. Щедрая прямо на новом minzoom (разреженная
      // выборка) и почти нулевая к z14, где место есть для каждой вершины.
      // Плавная шкала, а не фильтр по зуму, держит это непрерывным в
      // пределах диапазона: вершины появляются по одной по мере
      // освобождения места, а не вся популяция разом на пороге.
      "icon-allow-overlap": false,
      "icon-padding": ["interpolate", ["linear"], ["zoom"], 12.5, 12, 13, 7, 13.5, 4, 14, 2],

      // Собственная подпись вершины в том же символе. На туристической
      // карте высота - половина информации: "Пик Фурманова" мало что
      // значит без 3053 рядом, поэтому имя и высота стоят под треугольником.
      // Ниже z12 текст - пустая строка, бокс не ставится вовсе: на этом зуме
      // это обзор хребта, и смысл несут только треугольники.
      "text-field": buildPeakLabelField(ctx.t, ctx.lang),
      "text-font": MAP_FONT,
      "text-size": ["interpolate", ["linear"], ["zoom"], 12.5, 11, 15, 13],
      "text-anchor": "top",
      "text-offset": [0, 0.7],
      "text-padding": 3,
      "text-max-width": 7,
      // Треугольник - несущая половина: если имя не помещается, вершина
      // всё равно получает отметку, а не исчезает вместе с именем.
      // (icon-optional остаётся false - голое имя без треугольника это
      // ровно та проблема, ради которой слои объединили.)
      "text-optional": true,
      // Какие вершины переживут прореживание иначе было бы произвольно
      // (порядок обхода тайла). `ele` есть у 866 из 872, поэтому решает
      // высота: меньший sort key ставится первым, отсюда 5000 - ele, что
      // ставит Талгар (4979 м) в начало очереди, а безымянный отрог 2600 м -
      // в конец. Высота - слабый proxy известности в хребте, где 836 из 872
      // вершин выше 2500 м, но она стабильнее порядка тайла и держит
      // прореживание стабильным при панорамировании (одни и те же вершины
      // побеждают каждый кадр). Вершины без `ele` сортируются в конец, а не
      // роняют выражение на отсутствующем значении.
      // (Многоаргументная форма `to-number` - защита от null: пробует
      // аргументы по очереди, вершина без `ele` падает на 0.)
      "symbol-sort-key": ["-", 5000, ["to-number", ["get", "ele"], 0]],
    },
    paint: {
      "icon-opacity": fadeInFrom(12.5, 1),
      "text-color": PEAK_LABEL_COLOR,
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.8,
    },
  });

  // Перевалы - та же форма «иконка+подпись в одном символе», что и у вершин
  // выше, по той же причине коллизий. Размещён ПОСЛЕ pois-peak, чтобы там,
  // где перевал и вершина спорят за место, перевал побеждал: вершина - место,
  // на которое смотрят, перевал - место, через которое идут, и на карте
  // планирования маршрута второе важнее.
  m.addLayer({
    id: "pois-pass",
    type: "symbol",
    source: POIS_SOURCE,
    "source-layer": POIS_SOURCE,
    filter: ["all", ["==", ["get", "kind"], "mountain_pass"], ["!=", ["get", "pinned"], true]],
    // LOD: тот же порог, что и у pois-peak, по той же причине.
    minzoom: 12.5,
    layout: {
      "icon-image": PASS_ICON_IMAGE_ID,
      "icon-size": ["interpolate", ["linear"], ["zoom"], 12.5, 0.98, 13, 1.2, 15, 1.5],
      "icon-pitch-alignment": "viewport",
      "icon-rotation-alignment": "viewport",
      "icon-allow-overlap": false,
      "icon-padding": ["interpolate", ["linear"], ["zoom"], 12.5, 12, 13, 7, 13.5, 4, 14, 2],
      "text-field": buildPassLabelField(ctx.t, ctx.lang),
      "text-font": MAP_FONT,
      "text-size": ["interpolate", ["linear"], ["zoom"], 12.5, 10, 15, 12],
      "text-anchor": "top",
      "text-offset": [0, 0.9],
      "text-padding": 3,
      // Шире, чем у вершин: подпись перевала - имя плюс категория в одну
      // строку ("Талгарский провал 3А*"), при 9em переносило посреди категории.
      "text-max-width": 11,
      "text-optional": true,
      // Сначала перевалы с категорией, затем по высоте - прореживание на
      // низком зуме сохраняет задокументированную классику, а не случайную
      // выборку по порядку тайла.
      "symbol-sort-key": [
        "-",
        ["case", ["has", "difficulty"], 5000, 6000],
        ["to-number", ["get", "ele"], 0],
      ],
    },
    paint: {
      "icon-opacity": fadeInFrom(12.5, 1),
      "text-color": "#3b3b3b",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.3,
    },
  });

  // Опоры для power-lines-line выше, на том же LOD - не по общим
  // правилам кластеризации/подписей POI (см. запись power_tower в
  // POI_CLUSTER_EXCLUDED_KINDS). ЛЭП читается как инфраструктура именно
  // потому, что видно, на чём она держится; через обычный кластерный
  // конвейер каждая опора оказалась бы в анонимном бейдже до z13, а линия
  // видна с z10 - три уровня зума без единой отметки собственного маршрута.
  // `minzoom` скопирован прямо из power-lines-line, чтобы оба включались
  // вместе.
  //
  // allow-overlap+ignore-placement вместо коллизионного padding, как у
  // pois-peak выше - нужна каждая опора вдоль линии, а не прореженная
  // выборка «важных» (ранжирования тут нет, опоры взаимозаменяемы, в
  // отличие от вершин).
  m.addLayer({
    id: "pois-power-tower",
    type: "symbol",
    source: POIS_SOURCE,
    "source-layer": POIS_SOURCE,
    filter: ["all", ["==", ["get", "kind"], "power_tower"], ["!=", ["get", "pinned"], true]],
    minzoom: 10,
    layout: {
      "icon-image": poiIconImageId("power_tower"),
      "icon-size": ["interpolate", ["linear"], ["zoom"], 10, 0.5, 13, 0.75, 16, 1.05],
      "icon-pitch-alignment": "viewport",
      "icon-rotation-alignment": "viewport",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
    paint: {
      "icon-color": POI_COLOR,
      "icon-opacity": fadeInFrom(10, 1),
    },
  });

  // ── Двойники pois-peak/pois-pass для режима редактирования ───────────────
  // pois-peak/pois-pass выше читают POIS_SOURCE, которая никогда не несёт
  // скрытые строки (pois_tile фильтрует их на сервере) - поэтому показать
  // скрытую вершину/перевал полупрозрачной в режиме редактирования можно
  // только отдельным слоем на POIS_ADMIN_SOURCE, а не переключением
  // paint-свойства на том же слое (MapLibre не даёт менять `source` слоя
  // после создания). Оба слоя стартуют скрытыми (visibility: none) и
  // переключаются при входе/выходе из режима редактирования (см. watcher
  // editMode, MapView.vue) - никогда не загружаются/не рендерятся для не-редактора.
  m.addLayer({
    id: "pois-peak-admin",
    type: "symbol",
    source: POIS_ADMIN_SOURCE,
    "source-layer": POIS_ADMIN_SOURCE,
    filter: ["all", ["==", ["get", "kind"], "peak"], ["!=", ["get", "pinned"], true]],
    minzoom: 12.5,
    layout: {
      visibility: "none",
      "icon-image": PEAK_ICON_IMAGE_ID,
      "icon-size": ["interpolate", ["linear"], ["zoom"], 12.5, 0.8, 13, 0.95, 15, 1.25],
      "icon-pitch-alignment": "viewport",
      "icon-rotation-alignment": "viewport",
      "icon-allow-overlap": true,
      "text-field": buildPeakLabelField(ctx.t, ctx.lang),
      "text-font": MAP_FONT,
      "text-size": ["interpolate", ["linear"], ["zoom"], 12.5, 11, 15, 13],
      "text-anchor": "top",
      "text-offset": [0, 0.7],
      "text-padding": 3,
      "text-max-width": 7,
      "text-optional": true,
    },
    paint: {
      "icon-opacity": editIconOpacity(12.5),
      "text-color": PEAK_LABEL_COLOR,
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.8,
      "text-opacity": editIconOpacity(12.5),
    },
  });
  m.addLayer({
    id: "pois-pass-admin",
    type: "symbol",
    source: POIS_ADMIN_SOURCE,
    "source-layer": POIS_ADMIN_SOURCE,
    filter: ["all", ["==", ["get", "kind"], "mountain_pass"], ["!=", ["get", "pinned"], true]],
    minzoom: 12.5,
    layout: {
      visibility: "none",
      "icon-image": PASS_ICON_IMAGE_ID,
      "icon-size": ["interpolate", ["linear"], ["zoom"], 12.5, 0.98, 13, 1.2, 15, 1.5],
      "icon-pitch-alignment": "viewport",
      "icon-rotation-alignment": "viewport",
      "icon-allow-overlap": true,
      "text-field": buildPassLabelField(ctx.t, ctx.lang),
      "text-font": MAP_FONT,
      "text-size": ["interpolate", ["linear"], ["zoom"], 12.5, 10, 15, 12],
      "text-anchor": "top",
      "text-offset": [0, 0.9],
      "text-padding": 3,
      "text-max-width": 11,
      "text-optional": true,
    },
    paint: {
      "icon-opacity": editIconOpacity(12.5),
      "text-color": "#3b3b3b",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.3,
      "text-opacity": editIconOpacity(12.5),
    },
  });

  // Жёлтое кольцо «недавно изменено» для вершин/перевалов - тонкий круг
  // только-обводкой под иконкой, только в режиме редактирования. Вершины и
  // перевалы - обычные RGBA-битмапы (sdf: false, цвет запечён
  // buildPeakIconImage/buildPassIconImage), поэтому icon-halo-color/width
  // (работают только на SDF-иконках) их так пометить не могут, как это
  // делает circle-stroke-color у pois-icon-unclustered-halo для обычных
  // пиктограмм - отдельный слой кольца это эквивалент для не-SDF иконки.
  const RECENT_RING_COLOR = "#f5c518";
  m.addLayer({
    id: "pois-peak-recent-ring",
    type: "circle",
    source: POIS_ADMIN_SOURCE,
    "source-layer": POIS_ADMIN_SOURCE,
    filter: ["all", ["==", ["get", "kind"], "peak"], ["==", ["get", "recently_changed"], true]],
    minzoom: 12.5,
    layout: { visibility: "none" },
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 12.5, 9, 15, 13],
      "circle-color": "transparent",
      "circle-stroke-color": RECENT_RING_COLOR,
      "circle-stroke-width": 2,
      "circle-pitch-alignment": "viewport",
    },
  }, "pois-peak-admin");
  m.addLayer({
    id: "pois-pass-recent-ring",
    type: "circle",
    source: POIS_ADMIN_SOURCE,
    "source-layer": POIS_ADMIN_SOURCE,
    filter: ["all", ["==", ["get", "kind"], "mountain_pass"], ["==", ["get", "recently_changed"], true]],
    minzoom: 12.5,
    layout: { visibility: "none" },
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 12.5, 9, 15, 13],
      "circle-color": "transparent",
      "circle-stroke-color": RECENT_RING_COLOR,
      "circle-stroke-width": 2,
      "circle-pitch-alignment": "viewport",
    },
  }, "pois-pass-admin");

  // ~29K POI по KZ+KG, кластеризованы. peak/mountain_pass - свой symbol-слой
  // выше; mountain_rescue - пин (refreshPoiPinMarkers); `information`/
  // `bench`/`power_tower` скрыты или имеют свой слой (POI_CLUSTER_EXCLUDED_KINDS).
  // Всё остальное - общий набор пиктограмм (POI_ICON_SVG_PATHS) -
  // сгруппировано здесь, чтобы плотный участок туалетов/урн читался одним
  // чипом различимых глифов (refreshPoiClusterMarkers), а не крапом
  // отдельных точек. Старая пер-видовая шкала зума POI_MIN_ZOOM для этой
  // группы больше не нужна (кластеризация сама решает проблему плотности) -
  // вся группа включается разом на POI_LOD_MIN_ZOOM (жёсткий порог), но
  // остаётся актуальной для pois-label, который всё ещё читает её по видам.
  const poisIconImageExpr: any = [
    "match",
    ["get", "kind"],
    ...Object.keys(POI_ICON_SVG_PATHS).flatMap((kind) => [kind, poiIconImageId(kind)]),
    "",
  ];
  const IS_CLUSTER: any = ["has", "point_count"];

  registerPoiClusterImageResolver(m, clusterGlyphs);
  m.addLayer({
    id: POIS_CLUSTER_LAYER,
    type: "symbol",
    source: POIS_CLUSTER_SOURCE,
    filter: IS_CLUSTER,
    minzoom: POI_LOD_MIN_ZOOM,
    layout: {
      "icon-image": buildPoiClusterImageExpression(clusterGlyphs) as any,
      "icon-pitch-alignment": "viewport",
      "icon-rotation-alignment": "viewport",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });

  // Простой белый диск за каждой некластеризованной иконкой - пиктограммы
  // это тонированные SDF-фигуры одного цвета (buildSvgPathIconImage), без
  // подложки тёмная иконка над изолинией или тенью теряет контур.
  m.addLayer({
    id: "pois-icon-unclustered-halo",
    type: "circle",
    source: POIS_CLUSTER_SOURCE,
    filter: ["!", IS_CLUSTER],
    // Жёсткий LOD-порог - см. комментарий у POI_LOD_MIN_ZOOM.
    minzoom: POI_LOD_MIN_ZOOM,
    paint: {
      "circle-radius": [
        "interpolate",
        ["linear"],
        ["zoom"],
        9, 6.5,
        12, 9.2,
        15, 12.1,
        17, 13.8,
      ],
      "circle-color": "#ffffff",
      "circle-pitch-scale": "viewport",
      "circle-pitch-alignment": "viewport",
      "circle-opacity": clusterHiddenOpacity,
      // Тонкая волосяная линия POI_COLOR по умолчанию - у простого белого
      // диска не было своего края, весь контраст с рельефом держала только
      // иконка внутри. Заменяется (не складывается) кольцом «недавно
      // изменено», когда оно применимо - у слоя одна пара circle-stroke-*,
      // так что recently_changed побеждает целиком.
      "circle-stroke-color": ["case", ["==", ["get", "recently_changed"], true], RECENT_RING_COLOR, POI_COLOR],
      "circle-stroke-width": ["case", ["==", ["get", "recently_changed"], true], 2, 1.5],
      // clusterHiddenOpacity, а не просто затухание по зуму: обводка
      // должна гаснуть вместе с заливкой для скрытого POI в режиме
      // редактирования, а не оставаться в полную силу, пока диск под ней
      // гаснет до 0.4.
      "circle-stroke-opacity": clusterHiddenOpacity,
    },
  });
  m.addLayer({
    id: "pois-icon-unclustered",
    type: "symbol",
    source: POIS_CLUSTER_SOURCE,
    filter: ["!", IS_CLUSTER],
    // Жёсткий LOD-порог - см. комментарий у POI_LOD_MIN_ZOOM.
    minzoom: POI_LOD_MIN_ZOOM,
    layout: {
      "icon-image": poisIconImageExpr,
      "icon-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        9, 0.64,
        12, 0.86,
        15, 1.16,
        17, 1.35,
      ],
      "icon-pitch-alignment": "viewport",
      "icon-rotation-alignment": "viewport",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
    paint: {
      "icon-color": POI_COLOR,
      "icon-opacity": clusterHiddenOpacity,
    },
  });

  m.addLayer({
    id: "pois-label",
    type: "symbol",
    // POIS_CLUSTER_SOURCE (тот же GeoJSON, что кормит иконки/чипы), не
    // POIS_SOURCE - иначе подпись точки не знает, что её иконка сейчас
    // слита в кластерный чип, и продолжает висеть сама по себе.
    source: POIS_CLUSTER_SOURCE,
    minzoom: 12,
    filter: [
      "all",
      ["match", ["get", "kind"], ctx.poiClusterExcludedKinds, false, true],
      [">=", ["zoom"], POI_MIN_ZOOM],
      // Ниже POI_LOD_MIN_ZOOM иконки/чипы ещё не рендерятся вовсе (см. их
      // minzoom) - там подпись сама по себе штатно, это её ранний
      // "превью"-зум для редких видов. Выше - должна гаснуть вместе со
      // своей иконкой, если та сейчас слита в чип.
      ["any", ["<", ["zoom"], POI_LOD_MIN_ZOOM], ["!", IS_CLUSTER]],
    ],
    layout: {
      // Безымянный объект не получает ничего - пустая строка не рисует
      // подпись вовсе, а не пустой ореол. Это последняя ступень цепочки
      // coalesce.
      "text-field": buildNameField(ctx.lang),
      "text-font": MAP_FONT,
      "text-size": ["interpolate", ["linear"], ["zoom"], 12, 10, 15, 12],
      "text-offset": [0, 0.8],
      "text-anchor": "top",
      "text-padding": 3,
      "text-max-width": 9,
      // Хижина побеждает при коллизии родник/кран, оба побеждают урну:
      // меньший sort key ставится первым (POI_LABEL_SORT_KEY).
      "symbol-sort-key": POI_LABEL_SORT_KEY,
    },
    paint: {
      "text-color": "#3b3b3b",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.3,
    },
  });
};

// Подписи зданий/дамб и населённых пунктов - только именованные объекты.
const addLabelLayers = (m: maplibregl.Map, ctx: BasemapLayersContext) => {
  // Только именованные здания/дамбы - большинство контуров анонимны, подпись
  // на каждом была бы ровно тем захламлением, от которого спасает высокий
  // minzoom у buildings-fill. Это то, что делает «Медеу»/«Шымбулак» реально
  // читаемыми названиями места, а не просто фигурами на карте.
  m.addLayer({
    id: "buildings-label",
    type: "symbol",
    source: BUILDINGS_SOURCE,
    "source-layer": BUILDINGS_SOURCE,
    minzoom: 13,
    filter: ["has", "name"],
    layout: {
      "text-field": buildNameField(ctx.lang),
      "text-font": MAP_FONT,
      "text-size": ["interpolate", ["linear"], ["zoom"], 13, 10, 16, 12],
      "text-max-width": 8,
      "symbol-placement": "point",
    },
    paint: {
      "text-color": "#5a5044",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.3,
    },
  });

  // Двойник buildings-label для режима редактирования, та же форма, что у
  // pois-peak-admin/pois-pass-admin - читает BUILDINGS_ADMIN_SOURCE
  // (несёт `hidden`) вместо BUILDINGS_SOURCE, чтобы подпись скрытого здания
  // рендерилась полупрозрачной, а не просто пропадала, пока редактор решает.
  // Стартует скрытым, переключается вместе с pois-peak-admin/pois-pass-admin
  // (toggleEditMode, MapView.vue). Меняется только источник подписи -
  // buildings-fill/buildings-outline всегда читают BUILDINGS_SOURCE, режим
  // редактирования не трогает контур.
  m.addLayer({
    id: "buildings-label-admin",
    type: "symbol",
    source: BUILDINGS_ADMIN_SOURCE,
    "source-layer": BUILDINGS_ADMIN_SOURCE,
    minzoom: 13,
    filter: ["has", "name"],
    layout: {
      visibility: "none",
      "text-field": buildNameField(ctx.lang),
      "text-font": MAP_FONT,
      "text-size": ["interpolate", ["linear"], ["zoom"], 13, 10, 16, 12],
      "text-max-width": 8,
      "symbol-placement": "point",
    },
    paint: {
      "text-color": "#5a5044",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.3,
      "text-opacity": editIconOpacity(13),
    },
  });

  // Подписи водных объектов - реки/ручьи (symbol-placement "line", вдоль
  // русла) и озёра (landcover kind=water, точка в центроиде). Данные
  // (name*) уже были в тайлах, не хватало только клиентского слоя.
  m.addLayer({
    id: "waterways-label",
    type: "symbol",
    source: WATERWAYS_SOURCE,
    "source-layer": WATERWAYS_SOURCE,
    filter: ["has", "name"],
    layout: {
      "text-field": buildNameField(ctx.lang),
      "text-font": MAP_FONT,
      "text-size": ["interpolate", ["linear"], ["zoom"], 11, 10, 15, 13],
      "symbol-placement": "line",
      "symbol-spacing": 300,
      "text-max-angle": 30,
    },
    paint: {
      "text-color": RIVER_COLOR,
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.4,
    },
  });
  m.addLayer({
    id: "landcover-water-label",
    type: "symbol",
    source: LANDCOVER_SOURCE,
    "source-layer": LANDCOVER_SOURCE,
    minzoom: 11,
    filter: ["all", LANDCOVER_IS_WATER, ["has", "name"]],
    layout: {
      "text-field": buildNameField(ctx.lang),
      "text-font": MAP_FONT,
      "text-size": ["interpolate", ["linear"], ["zoom"], 11, 10, 15, 13],
      "symbol-placement": "point",
      "text-max-width": 8,
    },
    paint: {
      "text-color": RIVER_COLOR,
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.4,
    },
  });

  // Именованные населённые пункты (osm.places) - только подпись, без
  // маркера: в масштабе горного хребта город - ориентир по имени, а не
  // объект со своей иконкой. Размер/появление зависят от `kind`, а не от
  // колонки `population` напрямую - казахстанские теги population в OSM
  // редкие/ненадёжные вне крупных городов, `kind`
  // (city/town/village/hamlet/locality) - более стабильный сигнал веса имени.
  m.addLayer({
    id: "places-label",
    type: "symbol",
    source: PLACES_SOURCE,
    "source-layer": PLACES_SOURCE,
    minzoom: 8,
    filter: ["has", "name"],
    layout: {
      "text-field": buildNameField(ctx.lang),
      "text-font": MAP_FONT,
      // Один верхнеуровневый interpolate с match на каждой ступени, не
      // match из interpolate-ов - MapLibre отклоняет больше одного
      // zoom-based interpolate/step в одном выражении ("Only one zoom-based
      // ... subexpression may be used").
      "text-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        8,
        ["match", ["get", "kind"], "city", 13, "town", 11, 9],
        14,
        ["match", ["get", "kind"], "city", 17, "town", 15, 12],
      ],
      "text-max-width": 8,
      "symbol-placement": "point",
      "symbol-sort-key": [
        "match",
        ["get", "kind"],
        "city",
        0,
        "town",
        1,
        "village",
        2,
        "hamlet",
        3,
        "locality",
        4,
        5,
      ],
    },
    paint: {
      "text-color": "#4a453e",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.4,
    },
  });
};

// Точка входа - вызывается один раз из setupMapLayers (MapView.vue), сразу
// после создания карты. Порядок вызовов ниже - это и есть z-порядок
// MapLibre, совпадает с порядком addLayer() в исходном addBasemapLayers.
export const addBasemapLayers = (m: maplibregl.Map, ctx: BasemapLayersContext) => {
  const clusterGlyphs = buildPoiClusterGlyphs(ctx.poiClusterExcludedKinds);
  addSources(m, clusterGlyphs);
  addLandcoverAndHillshadeLayers(m);
  addBoundariesAndWaterwayLayers(m, ctx);
  addContourLayers(m);
  addCliffsAndRidgeLayers(m);
  addBuildingLayers(m);
  addRoadsAerialwaysPowerLineLayers(m);
  addTrailLayers(m);
  // Позже добавленный слой выигрывает и коллизию, и z-order - labels перед
  // POI, иначе водные подписи перекрывают POI.
  addLabelLayers(m, ctx);
  addPoiLayers(m, ctx, clusterGlyphs);
  // Границы охраняемых территорий - поверх всей карты: граница часто идёт по
  // реке или тропе, и их линии не должны её перекрывать. (Слои маршрутов
  // MapView добавляет позже и они остаются выше.)
  for (const id of ["boundaries-line", "boundaries-line-near", "boundaries-label"]) m.moveLayer(id);
};
